import React from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag } from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { useCart } from "@/context/CartContext";
import { resolveImage, formatPrice } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, total, count, clear } = useCart();

  return (
    <Layout>
      <div className="px-4 pt-6 pb-8">
        <h1 className="font-heading font-black text-2xl text-brand-blue">Carrello</h1>
        {items.length === 0 ? (
          <div className="mt-12 text-center">
            <div className="mx-auto w-20 h-20 rounded-full bg-brand-yellow/20 flex items-center justify-center">
              <ShoppingBag className="w-10 h-10 text-brand-blue" strokeWidth={2} />
            </div>
            <p className="mt-4 text-slate-500">Il tuo carrello è vuoto</p>
            <Link
              to="/catalogo"
              data-testid="cart-empty-cta"
              className="mt-5 inline-flex items-center gap-2 bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold rounded-full px-5 py-3 text-sm"
            >
              Scopri il catalogo →
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-slate-500 mt-1">{count} {count === 1 ? "articolo" : "articoli"}</p>
            <div className="mt-4 space-y-3">
              {items.map((it) => (
                <div key={it.id} data-testid={`cart-item-${it.id}`} className="bg-white border border-border rounded-2xl p-3 flex gap-3">
                  <div className="w-20 h-20 rounded-xl overflow-hidden bg-slate-100 flex-shrink-0">
                    {it.image && <img src={resolveImage(it.image)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <Link to={`/prodotto/${it.id}`} className="font-heading font-bold text-sm text-slate-900 line-clamp-2">
                      {it.title}
                    </Link>
                    <div className="mt-1 font-heading font-black text-brand-blue">{formatPrice(it.price)}</div>
                    <div className="mt-2 flex items-center justify-between">
                      <div className="flex items-center gap-1">
                        <button
                          data-testid={`cart-decrement-${it.id}`}
                          onClick={() => updateQty(it.id, it.qty - 1)}
                          disabled={it.qty <= 1}
                          className="w-8 h-8 rounded-full border border-border flex items-center justify-center disabled:opacity-40"
                        >
                          <Minus className="w-4 h-4" />
                        </button>
                        <span data-testid={`cart-qty-${it.id}`} className="w-8 text-center font-bold">{it.qty}</span>
                        <button
                          data-testid={`cart-increment-${it.id}`}
                          onClick={() => updateQty(it.id, it.qty + 1)}
                          disabled={it.qty >= it.max}
                          className="w-8 h-8 rounded-full border border-border flex items-center justify-center disabled:opacity-40"
                        >
                          <Plus className="w-4 h-4" />
                        </button>
                      </div>
                      <button
                        data-testid={`cart-remove-${it.id}`}
                        onClick={() => removeItem(it.id)}
                        className="text-slate-400 hover:text-red-500"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-6 bg-white border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between">
                <span className="text-slate-600 font-medium">Totale</span>
                <span data-testid="cart-total" className="font-heading font-black text-2xl text-brand-blue">
                  {formatPrice(total)}
                </span>
              </div>
              <Button
                data-testid="cart-checkout-btn"
                onClick={() => (window.location.href = "/checkout")}
                className="w-full mt-4 h-12 rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-heading font-bold"
              >
                Procedi al checkout
              </Button>
              <button
                data-testid="cart-clear-btn"
                onClick={clear}
                className="w-full mt-3 text-xs text-slate-500 hover:text-red-500"
              >
                Svuota carrello
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}
